import { miniButtonDockScrollBottomPad } from '@focuslee/shared-mini-app';
import { Box, Stack } from '@mui/material';
import { Placeholder } from '@telegram-apps/telegram-ui';
import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { allIconNames } from '../../helpers/common';
import { matchesSemantically } from '../../helpers/iconSemantics';
import { getButtonThemeColors } from '../../helpers/theme';
import { Page } from '../Page';
import { CommonIcon } from './CommonIcon';
import { SearchableButtonDock } from './SearchableButtonDock';

interface IconPickerSettingsProps {
  initialIcon: string;
  isLoading?: boolean;
  onSave: (settings: { icon: string }) => Promise<void>;
  onCancel: () => void;
}

export const IconPickerSettings: FC<IconPickerSettingsProps> = ({
  initialIcon,
  isLoading = false,
  onSave,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [selectedIcon, setSelectedIcon] = useState<string>(initialIcon);
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [searchOpen, setSearchOpen] = useState(false);

  const filteredIcons = useMemo(() => {
    if (!searchFilter.trim()) {
      return allIconNames;
    }
    const searchLower = searchFilter.toLowerCase();
    return allIconNames.filter(icon =>
      icon.toLowerCase().includes(searchLower) || matchesSemantically(icon, searchFilter)
    );
  }, [searchFilter]);

  const handleSave = useCallback(async () => {
    await onSave({ icon: selectedIcon });
  }, [selectedIcon, onSave]);

  return (
    <Page
      back={true}
      insetEnabled={false}
      edgeToEdge
      backClick={onCancel}
      footer={
        <SearchableButtonDock
          secondary={{ text: t('actions.cancel'), onClick: onCancel, navigation: 'back' }}
          main={{
            text: t('actions.save'),
            onClick: handleSave,
            loading: isLoading,
            bgColor: getButtonThemeColors(true).backgroundColor,
          }}
          searchOpen={searchOpen}
          searchQuery={searchFilter}
          searchPlaceholder={t('picker.searchIcon')}
          searchAriaLabel={t('picker.searchIcons')}
          closeSearchAriaLabel={t('picker.closeSearch')}
          onSearchQueryChange={setSearchFilter}
          onOpenSearch={() => setSearchOpen(true)}
          onCloseSearch={() => {
            setSearchOpen(false);
            setSearchFilter('');
          }}
        />
      }
    >
      <Box sx={{ height: '100%', position: 'relative', overflow: 'hidden' }}>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            WebkitOverflowScrolling: 'touch',
            paddingTop: 'calc(var(--tg-viewport-content-safe-area-inset-top, 0px) + var(--tg-viewport-safe-area-inset-top, 0px))',
          }}
        >
          {filteredIcons.length === 0 ? (
            <Stack direction="column" justifyContent="center" alignItems="center" height="100%">
              <Placeholder
                header={t('picker.noIconsFound')}
                description={t('picker.tryDifferentSearch')}
                style={{ padding: 0 }}
              >
                <img
                  alt="Telegram sticker"
                  src="https://focuslee-production-images.s3.eu-west-1.amazonaws.com/assets/telegram.gif"
                  style={{ display: 'block', width: '144px', height: '144px' }}
                />
              </Placeholder>
            </Stack>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(56px, 1fr))',
                paddingTop: '10px',
                paddingBottom: miniButtonDockScrollBottomPad,
              }}
            >
              {filteredIcons.map((iconName) => (
                <Box
                  key={iconName}
                  sx={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '10px 0',
                  }}
                  onClick={() => setSelectedIcon(iconName)}
                >
                  <CommonIcon
                    icon={iconName}
                    type="filled"
                    size={30}
                    color={iconName === selectedIcon ? 'var(--tg-theme-button-color)' : 'var(--tg-theme-text-color)'}
                  />
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Page>
  );
};
